<template>
  <BasicNavbar />
  <div
    class="w-full flex justify-center px-4 pb-8 mt-[30px] tablet-v:mt-[56px] tablet-h:mt-[90px]"
  >
    <div class="w-full max-w-[410px]">
      <div class="mb-10">
        <h1
          class="text-[26px] font-medium leading-[30px] tracking-[-0.325px] text-gray-800"
        >
          Protect what's yours.
        </h1>
        <p
          class="mt-[4px] text-[26px] font-medium leading-[30px] tracking-[-0.325px] text-gray-800/55"
        >
          Create your Dacr account.
        </p>
      </div>

      <form class="flex flex-col" @submit.prevent="handleSignUp">
        <div class="flex gap-[10px] mb-[14px]">
          <div class="flex-1">
            <Input
              v-model="firstName"
              size="xl"
              label="First name"
              :error="firstNameError"
              :disabled="loading"
              :tabindex="1"
            />
          </div>
          <div class="flex-1">
            <Input
              v-model="lastName"
              size="xl"
              label="Last name"
              :error="lastNameError"
              :disabled="loading"
              :tabindex="2"
            />
          </div>
        </div>
        <div class="mb-[14px]">
          <Input
            v-model="email"
            size="xl"
            label="Email"
            type="email"
            :error="emailError"
            :disabled="loading"
            :tabindex="3"
          />
        </div>
        <div class="mb-[14px]">
          <Input
            v-model="username"
            size="xl"
            label="Username"
            :error="usernameError"
            :disabled="loading"
            :tabindex="4"
          />
        </div>
        <div class="mb-[14px]">
          <Input
            v-model="password"
            size="xl"
            label="Password"
            type="password"
            :error="passwordError"
            :disabled="loading"
            :tabindex="5"
            @focus="handlePasswordFocus"
          />
        </div>
        <div v-if="showConfirmPassword" class="mb-[14px]">
          <Input
            v-model="confirmPassword"
            size="xl"
            label="Confirm password"
            type="password"
            :error="confirmPasswordError"
            :disabled="loading"
            :tabindex="6"
          />
        </div>

        <p class="mb-[14px] text-[13px] leading-[18px] text-gray-800">
          By selecting "Agree and sign up" I agree to the
          <a href="/legal/terms" class="text-link">Dacr Terms</a>
          and
          <a href="/legal/privacy" class="text-link">Privacy Policy</a>.
        </p>

        <Button
          class="w-full"
          variant="primary"
          size="lg"
          :loading="loading"
          @click="handleSignUp"
        >
          Agree and sign up
        </Button>
        <button
          type="submit"
          class="hidden"
          aria-hidden="true"
          tabindex="-1"
          :disabled="loading"
        />
      </form>

      <p class="mt-[30px] text-[14px] leading-[22px] text-center text-gray-800">
        Have a Dacr account?
        <span class="text-link" :class="linkClasses" @click="handleLoginClick">
          Sign in
        </span>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  SearchSort,
  useCreateUserMutation,
  useValidateOtpMutation,
} from '@/shared/graphql/legacy/generated'
import { useExplorerStore } from '@/features/explorer/store'
import {
  clearPricingCheckout,
  startPricingCheckout,
} from '@/features/onboarding/pricingCheckout'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useUserStore } from '@/shared/stores/user'
import { analytics } from '@/shared/services/analytics'
import { EMAIL_REGEX, PASSWORD_REGEX } from '@/shared/utils/string'
import { signup } from '@dacr/auth'
import { Input, Button } from '@dacr/library'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BasicNavbar from '@/shared/components/BasicNavbar.vue'

const { mutate: validateOTP } = useValidateOtpMutation()
const { mutate: createUser } = useCreateUserMutation()

const route = useRoute()
const currentFolderStore = useExplorerStore()
const userStore = useUserStore()
const router = useRouter()
const toastStore = useToastStore()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const username = ref('')
const password = ref('')
const confirmPassword = ref('')

const showConfirmPassword = ref(false)

const firstNameError = ref('')
const lastNameError = ref('')
const emailError = ref('')
const usernameError = ref('')
const passwordError = ref('')
const confirmPasswordError = ref('')

const loading = ref(false)

onMounted(() => {
  const url = new URL(window.location.href)
  const emailParam = url.searchParams.get('email')
  const planParam = url.searchParams.get('plan')

  if (planParam) {
    startPricingCheckout(planParam)
  } else {
    clearPricingCheckout()
  }

  if (!emailParam) {
    return
  }

  email.value = emailParam
})

watch(firstName, () => {
  firstNameError.value = ''
})

watch(lastName, () => {
  lastNameError.value = ''
})

watch(email, () => {
  emailError.value = ''
})

watch(username, () => {
  usernameError.value = ''
})

watch(password, () => {
  passwordError.value = ''
  confirmPasswordError.value = ''
})

watch(confirmPassword, () => {
  confirmPasswordError.value = ''
})

const linkClasses = computed(() => ({
  ...(loading.value && {
    'pointer-events-none': true,
    'opacity-50': true,
  }),
}))

const handleLoginClick = () => {
  router.push({
    path: '/login',
    query: {
      ott: route.query.ott,
      redirect: route.query.redirect,
    },
  })
}

const handlePasswordFocus = () => {
  showConfirmPassword.value = true
}

const validate = () => {
  let hasError = false

  if (!firstName.value.trim()) {
    firstNameError.value = 'Please enter your first name'
    hasError = true
  }

  if (!/^[A-Za-z\s]+$/.test(firstName.value)) {
    firstNameError.value = 'First name can only contain letters and spaces'
    hasError = true
  }

  if (!lastName.value.trim()) {
    lastNameError.value = 'Please enter your last name'
    hasError = true
  }

  if (!/^[A-Za-z\s]+$/.test(lastName.value)) {
    lastNameError.value = 'Last name can only contain letters and spaces'
    hasError = true
  }

  if (!email.value.trim()) {
    emailError.value = 'Please enter your email address'
    hasError = true
  }

  if (email.value.trim() && !EMAIL_REGEX.test(email.value.trim())) {
    emailError.value = 'Email entered is not valid'
    hasError = true
  }

  if (!username.value.trim()) {
    usernameError.value = 'Please enter a username'
    hasError = true
  }

  if (!/^[A-Za-z0-9._]+$/.test(username.value)) {
    usernameError.value =
      'Username can only contain letters, numbers, periods, and underscores'
    hasError = true
  }

  if (!password.value.trim()) {
    passwordError.value = 'Please enter a password'
    hasError = true
  }

  if (password.value && !PASSWORD_REGEX.test(password.value.trim())) {
    passwordError.value =
      'Password must be 8 characters or more including at least 1 number and 1 special character'
    hasError = true
  }

  if (!confirmPassword.value.trim()) {
    confirmPasswordError.value = 'Please enter a password.'
    hasError = true
  }

  if (
    confirmPassword.value &&
    password.value.trim() !== confirmPassword.value.trim()
  ) {
    confirmPasswordError.value = 'Confirmation password does not match password'
    hasError = true
  }

  return !hasError
}

const handleSignUp = async () => {
  if (!validate()) return

  try {
    loading.value = true

    const referralCode = document.cookie.match(/__aff-id=([a-zA-Z0-9_-]+)/)?.[1]

    await signup({
      email: email.value,
      password: password.value,
      client: 'web',
    })

    const result = await createUser({
      input: {
        email: email.value,
        firstName: firstName.value.trim(),
        lastName: lastName.value.trim(),
        username: username.value.trim(),
        ...(referralCode && {
          referralCode,
        }),
      },
    })

    if (result?.data?.createUser) {
      const user = result.data.createUser

      analytics.trackSignupConversion(user.id)

      if (route.query.ott) {
        await validateOTP({
          code: route.query.ott as string,
        })

        router.push('/auth/success')

        return
      }

      userStore.setUser(user)
      userStore.isSignedIn = true

      // Check for OAuth redirect
      const redirectUrl = route.query.redirect as string

      if (redirectUrl) {
        window.location.href = redirectUrl
        return
      }

      currentFolderStore.setCurrentFolder({
        __typename: 'FolderByPathResponse',
        id: 'root',
        items: [],
        name: '/',
        next: null,
        numberOfItems: 0,
        parentFolderId: '',
        parentPath: '',
        timestamp: null,
        sortBy: SearchSort.NameAsc,
      })

      router.push('/register/organization')
    }
  } catch (error) {
    if (error instanceof Error) {
      console.error(error)

      if (error.message.toLowerCase().includes('first')) {
        firstNameError.value = error.message
        return
      }

      if (error.message.toLowerCase().includes('last')) {
        lastNameError.value = error.message
        return
      }

      if (error.message.toLowerCase().includes('email')) {
        emailError.value = error.message
        return
      }

      if (error.message.toLowerCase().includes('username')) {
        usernameError.value = error.message
        return
      }

      toastStore.showToast({
        type: ToastType.Error,
        content: error.message,
        error,
      })
    }
  } finally {
    loading.value = false
  }
}
</script>
